/**
 * MarkerCompat — Drop-in replacement for google.maps.Marker
 * that uses AdvancedMarkerElement when available, with transparent
 * legacy fallback.
 *
 * Public surface intentionally mirrors the subset of google.maps.Marker
 * used across the codebase:
 *   setPosition / getPosition / position (getter/setter)
 *   setIcon
 *   setMap / getMap / map (getter/setter)
 *   setVisible
 *   setLabel
 *   setDraggable
 *   addListener  (click, drag, dragstart, dragend, rightclick,
 *                  mouseover, mouseout, position_changed)
 */

/* ──────────── helpers ──────────── */

/** Numeric value from google.maps.Point or plain {x,y} */
const pt = (p, axis) => {
  if (!p) return 0;
  return axis === 'x' ? (p.x ?? 0) : (p.y ?? 0);
};

/** Numeric value from google.maps.Size or plain {width,height} */
const sz = (s, dim) => {
  if (!s) return null;
  return dim === 'w' ? (s.width ?? null) : (s.height ?? null);
};

/**
 * Convert a legacy icon config into an HTMLElement for
 * AdvancedMarkerElement.content.
 *
 * Supported shapes:
 *  - string URL
 *  - { url, scaledSize?, size?, anchor? }        (google.maps.Icon)
 *  - { path, scale?, fillColor?, rotation?, …  } (google.maps.Symbol)
 *  - HTMLElement  (pass-through)
 */
const iconToContent = (icon) => {
  if (!icon) return undefined;
  if (icon instanceof HTMLElement) return icon;

  let url, w, h, ax, ay;

  if (typeof icon === 'string') {
    url = icon;
    w = 30; h = 30; ax = 15; ay = 30;

  } else if (icon.path) {
    /* google.maps.Symbol → inline SVG data-URI */
    const scale = icon.scale || 1;
    w = Math.ceil(24 * scale);
    h = Math.ceil(24 * scale);
    const rot = parseInt(icon.rotation) || 0;
    ax = icon.anchor ? pt(icon.anchor, 'x') * scale : w / 2;
    ay = icon.anchor ? pt(icon.anchor, 'y') * scale : h / 2;

    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 24 24">`,
      `<g transform="rotate(${rot} 12 12)">`,
      `<path d="${icon.path.trim()}"`,
      ` fill="${icon.fillColor || 'black'}"`,
      ` fill-opacity="${icon.fillOpacity ?? 1}"`,
      ` stroke="${icon.strokeColor || 'none'}"`,
      ` stroke-width="${icon.sstrokeWeight || icon.strokeWeight || 0}"/>`,
      `</g></svg>`,
    ].join('');

    url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

  } else if (icon.url) {
    url = icon.url;
    w = sz(icon.scaledSize, 'w') || sz(icon.size, 'w') || 30;
    h = sz(icon.scaledSize, 'h') || sz(icon.size, 'h') || 30;
    ax = icon.anchor ? pt(icon.anchor, 'x') : w / 2;
    ay = icon.anchor ? pt(icon.anchor, 'y') : h;

  } else {
    return undefined;
  }

  const img = document.createElement('img');
  img.src = url;
  img.width = w;
  img.height = h;
  img.style.display = 'block';

  /*
   * Anchor compensation:
   *   AME places the geographic point at the bottom-centre of the content.
   *   We need it at pixel (ax, ay) of the icon instead.
   *     dx = (w / 2) - ax      shift horizontally
   *     dy = ay - h             shift vertically (negative = up)
   */
  const dx = Math.round(w / 2 - ax);
  const dy = Math.round(ay - h);
  if (dx || dy) {
    img.style.transform = `translate(${dx}px, ${dy}px)`;
  }

  return img;
};

/**
 * Build a content element combining icon + text label
 * (used by cluster markers).
 */
const buildLabelContent = (icon, label) => {
  const container = document.createElement('div');
  container.style.position = 'relative';
  container.style.display = 'inline-flex';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';

  const iconEl = iconToContent(icon);
  if (iconEl) container.appendChild(iconEl);

  const text = typeof label === 'string' ? label : label?.text || '';
  if (text) {
    const lbl = document.createElement('div');
    Object.assign(lbl.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: (typeof label === 'object' ? label.color : null) || 'black',
      fontSize: (typeof label === 'object' ? label.fontSize : null) || '11px',
      fontWeight: (typeof label === 'object' ? label.fontWeight : null) || 'normal',
      fontFamily: 'Arial, sans-serif',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    });
    lbl.textContent = text;
    container.appendChild(lbl);
  }

  return container;
};

/* ──────────── MarkerCompat ──────────── */

class MarkerCompat {
  /**
   * @param {Object} options  Same shape as google.maps.MarkerOptions
   */
  constructor(options = {}) {
    this._posChangedCbs = [];
    this._labelEl = null;
    this._iconConfig = options.icon ?? null;
    this._visible = options.visible !== false;

    /* Carry-over any custom properties (e.g. id, info, …) */
    const reserved = new Set([
      'position', 'map', 'draggable', 'icon', 'title',
      'zIndex', 'optimized', 'visible', 'crossOnDrag',
      'animation', 'label',
    ]);
    for (const key of Object.keys(options)) {
      if (!reserved.has(key)) this[key] = options[key];
    }

    /*
     *
     * Kept disabled (false) because the project renders 10,000+ markers
     * simultaneously. Legacy Marker with optimized:true uses a single
     * canvas layer, while Advanced Markers create one DOM node per marker
     * which degrades performance at this scale.
     *
     * To enable: change to true (only recommended for views with < 500 markers)
     */
    const USE_ADVANCED_MARKERS = false;
    const AME = USE_ADVANCED_MARKERS && google?.maps?.marker?.AdvancedMarkerElement;

    if (AME) {
      /* ── Advanced path ── */
      const content = options.label
        ? buildLabelContent(options.icon, options.label)
        : iconToContent(options.icon);

      if (!this._visible && content) {
        content.style.display = 'none';
      }

      this._ame = new AME({
        position: options.position,
        map: this._visible ? (options.map ?? null) : null,
        title: options.title || '',
        zIndex: options.zIndex ?? 10,
        content: content ?? undefined,
        gmpDraggable: options.draggable ?? false,
      });

      this._mapRef = options.map ?? null;
      this._legacy = null;

    } else {
      /* ── Legacy fallback (active until Map ID is configured) ── */
      this._ame = null;
      this._mapRef = null;
      this._legacy = new google.maps.Marker(options); // eslint-disable-line no-undef
    }
  }

  /* ─── Position ─── */

  getPosition() {
    if (this._legacy) return this._legacy.getPosition();
    const pos = this._ame.position;
    if (!pos) return null;
    if (typeof pos.lat === 'function') return pos;
    return new google.maps.LatLng(pos.lat, pos.lng); // eslint-disable-line no-undef
  }

  setPosition(position) {
    if (this._legacy) {
      this._legacy.setPosition(position);
    } else {
      this._ame.position = position;
    }
    for (const cb of this._posChangedCbs) cb();
  }

  /** MarkerClusterer reads/writes .position directly */
  get position() {
    return this._ame?.position ?? this._legacy?.getPosition?.();
  }
  set position(val) { this.setPosition(val); }

  /* ─── Map ─── */

  setMap(map) {
    this._mapRef = map;
    if (this._legacy) {
      this._legacy.setMap(map);
    } else {
      this._ame.map = map;
    }
  }

  getMap() {
    if (this._legacy) return this._legacy.getMap();
    return this._ame.map;
  }

  get map() { return this.getMap(); }
  set map(val) { this.setMap(val); }

  /* ─── Icon ─── */

  setIcon(icon) {
    this._iconConfig = icon;
    if (this._legacy) {
      this._legacy.setIcon(icon);
      return;
    }
    const content = iconToContent(icon);
    if (content && !this._visible) content.style.display = 'none';
    this._ame.content = content ?? undefined;
  }

  /* ─── Visibility ─── */

  getVisible() {
    if (this._legacy) return this._legacy.getVisible();
    return this._visible;
  }

  setVisible(visible) {
    this._visible = visible;
    if (this._legacy) {
      this._legacy.setVisible(visible);
      return;
    }
    if (this._ame.content) {
      this._ame.content.style.display = visible ? '' : 'none';
    }
    if (!visible) {
      this._ame.map = null;
    } else if (this._mapRef) {
      this._ame.map = this._mapRef;
    }
  }

  /* ─── Label ─── */

  setLabel(label) {
    if (this._legacy) {
      this._legacy.setLabel(label);
      return;
    }
    const text = !label ? '' : (typeof label === 'string' ? label : label.text || '');

    if (!text) {
      if (this._labelEl) { this._labelEl.remove(); this._labelEl = null; }
      return;
    }

    if (!this._labelEl) {
      this._labelEl = document.createElement('div');
      Object.assign(this._labelEl.style, {
        position: 'absolute',
        whiteSpace: 'nowrap',
        left: '50%',
        bottom: '100%',
        transform: 'translateX(-50%)',
        padding: '2px 6px',
        background: 'white',
        borderRadius: '3px',
        boxShadow: '0 1px 4px rgba(0,0,0,.3)',
        pointerEvents: 'none',
        zIndex: '1',
      });
      this._ame.element?.appendChild(this._labelEl);
    }

    this._labelEl.textContent = text;
    if (typeof label === 'object') {
      if (label.color) this._labelEl.style.color = label.color;
      if (label.fontSize) this._labelEl.style.fontSize = label.fontSize;
    }
  }

  /* ─── Draggable ─── */

  setDraggable(val) {
    if (this._legacy) {
      this._legacy.setDraggable(val);
    } else {
      this._ame.gmpDraggable = val;
    }
  }

  /* ─── Events ─── */

  addListener(event, handler) {
    if (this._legacy) return this._legacy.addListener(event, handler);

    switch (event) {
      case 'position_changed': {
        this._posChangedCbs.push(handler);
        return {
          remove: () => {
            const i = this._posChangedCbs.indexOf(handler);
            if (i > -1) this._posChangedCbs.splice(i, 1);
          },
        };
      }

      case 'rightclick': {
        const el = this._ame.element;
        if (el) {
          const wrapped = (e) => { e.preventDefault(); handler(e); };
          el.addEventListener('contextmenu', wrapped);
          return { remove: () => el.removeEventListener('contextmenu', wrapped) };
        }
        return { remove: () => {} };
      }

      case 'mouseover': {
        const el = this._ame.element;
        if (el) {
          el.addEventListener('mouseenter', handler);
          return { remove: () => el.removeEventListener('mouseenter', handler) };
        }
        return { remove: () => {} };
      }

      case 'mouseout': {
        const el = this._ame.element;
        if (el) {
          el.addEventListener('mouseleave', handler);
          return { remove: () => el.removeEventListener('mouseleave', handler) };
        }
        return { remove: () => {} };
      }

      default:
        /* click, drag, dragstart, dragend — supported natively by AME */
        return this._ame.addListener(event, handler);
    }
  }

  /* ─── Title ─── */

  getTitle() {
    return this._ame?.title ?? this._legacy?.getTitle?.() ?? '';
  }
  setTitle(val) {
    if (this._legacy) this._legacy.setTitle(val);
    else if (this._ame) this._ame.title = val;
  }
  get title() { return this.getTitle(); }
  set title(val) { this.setTitle(val); }

  /* ─── zIndex ─── */

  getZIndex() {
    return this._ame?.zIndex ?? this._legacy?.getZIndex?.() ?? 0;
  }
  setZIndex(val) {
    if (this._legacy) this._legacy.setZIndex(val);
    else if (this._ame) this._ame.zIndex = val;
  }

  /* ─── Element access (for direct DOM work) ─── */

  get element() {
    return this._ame?.element ?? null;
  }
}

export { MarkerCompat, iconToContent };
